import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MessagingPage from './MessagingPage';
import PostsShellLayout from '../../support/openDiscussions/components/shell/PostsShellLayout';
import PostsMobileFloatingChrome from '../../support/openDiscussions/components/shell/PostsMobileFloatingChrome';
import MessagesFollowSheet from './MessagesFollowSheet';
import { messagingThreadPath } from './messagingUserUtils';
import { useMessagingUnreadCount } from './useMessagingUnreadCount';
import { APP_NAME } from '../../../config/branding';
import { usePostsShellLayout } from '../../support/openDiscussions/hooks/usePostsShellLayout';
import { usePostsMobileScrollChrome } from '../../support/openDiscussions/hooks/usePostsMobileScrollChrome';
import { isSocialMobileChromeRoute } from '../../support/openDiscussions/lib/socialMobileChromeRoutes';
import { defaultHomeFeedSort } from '../../support/openDiscussions';
import { buildSigninUrl, stashPendingVerificationEmail } from '../../support/openDiscussions/auth/guestAuth';
import { useNotification } from '../../../layout/ReusableComponent/PopupNotification/PopupNotification';
import '../../support/openDiscussions/styles/OpenDiscussions.css';
import '../../../GlobalPosts/PostsPage.css';
import './MessagingPageShell.css';

const baseUrl = process.env.REACT_APP_BASE_URL || '';

/** Full messages inside the social / posts shell (cluster: social + chat). */
export default function MessagingPageShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const shell = usePostsShellLayout({
    returnTo: '/sm/all-messages',
    addNotification,
    enableWheelScroll: false,
  });
  const {
    profile,
    profileResolved,
    profileAvatarSrc,
    isMobileLayout,
    mobileDrawerOpen,
    goPosts,
    closeDrawer,
    navigate: shellNavigate,
  } = shell;

  const isEmailVerificationPending = profileResolved && profile?.emailNotVerified === true;

  useEffect(() => {
    if (!isEmailVerificationPending) return undefined;
    let cancelled = false;
    if (profile?.email) stashPendingVerificationEmail(profile.email);
    (async () => {
      try {
        await fetch(`${baseUrl}/api/signout`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
      } catch {
        /* ignore */
      }
      if (!cancelled) {
        navigate(buildSigninUrl({ mode: 'signin', returnTo: '/posts' }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEmailVerificationPending, profile?.email, navigate]);

  const isSocialRoute = isSocialMobileChromeRoute(location.pathname);
  const messagingSearchParams = new URLSearchParams(location.search);
  const inMessagingThread = Boolean(
    messagingSearchParams.get('userId') || messagingSearchParams.get('compose') === '1'
  );
  const hideMobileFloatingChrome = mobileDrawerOpen || inMessagingThread;
  const [mobileFollowSheetOpen, setMobileFollowSheetOpen] = useState(false);
  const { hasUnread: hasUnreadMessages, refreshUnreadCount } = useMessagingUnreadCount(baseUrl);
  const mobileFloatingChromeEnabled = isMobileLayout && isSocialRoute && !mobileDrawerOpen;
  const mobileScrollChromeMode = usePostsMobileScrollChrome(
    mobileFloatingChromeEnabled,
    `${location.pathname}${location.search}`
  );

  useEffect(() => {
    document.title = `Messages · ${APP_NAME}`;
    refreshUnreadCount();
    return () => {
      document.title = APP_NAME;
      refreshUnreadCount();
    };
  }, [refreshUnreadCount]);

  const openMobileHome = useCallback(() => {
    goPosts({ sort: defaultHomeFeedSort() });
    closeDrawer();
  }, [goPosts, closeDrawer]);

  const openMobileCreate = useCallback(() => {
    goPosts({ compose: '1' });
    closeDrawer();
  }, [goPosts, closeDrawer]);

  const openMobileMessages = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  const openMobileSocial = useCallback(() => {
    setMobileFollowSheetOpen(true);
    closeDrawer();
  }, [closeDrawer]);

  const handleFollowSheetSelectUser = useCallback(
    (user) => {
      setMobileFollowSheetOpen(false);
      if (!user?.userId) return;
      navigate(messagingThreadPath(user));
    },
    [navigate]
  );

  const openMobileProfile = useCallback(() => {
    const uid = String(profile?.userId || '').trim();
    if (!uid) {
      shellNavigate(buildSigninUrl({ mode: 'signup', returnTo: '/sm/all-messages' }));
      closeDrawer();
      return;
    }
    const mode =
      String(profile?.profileMode || 'personal').trim().toLowerCase() === 'social' ? 'social' : 'personal';
    shellNavigate(
      `/posts?view=user-profile&userId=${encodeURIComponent(uid)}&profileMode=${encodeURIComponent(mode)}`
    );
    closeDrawer();
  }, [profile, shellNavigate, closeDrawer]);

  const openPeerProfileFromMessaging = useCallback(
    (peer) => {
      const uid = String(peer?.userId || '').trim();
      if (!uid) return;
      const mode =
        String(peer?.profileMode || 'personal').trim().toLowerCase() === 'social' ? 'social' : 'personal';
      const handle = String(peer?.handle || '').trim();
      const params = new URLSearchParams({
        view: 'user-profile',
        userId: uid,
        profileMode: mode,
      });
      if (handle) params.set('handle', handle);
      navigate(`/posts?${params.toString()}`);
      closeDrawer();
    },
    [closeDrawer, navigate]
  );

  return (
    <>
      <PostsShellLayout
        shell={shell}
        layoutClassName="posts-page-layout--messages"
        mainClassName="posts-page-layout__center messaging-page-shell-main"
        groupedMobileTools
        messagesPageActive
        sidebarProps={{ isMessagesActive: true }}
        returnTo="/sm/all-messages"
      >
        <MessagingPage
          mobileFullscreen={isMobileLayout}
          viewerUserId={profile?.userId}
          viewerUser={{
            userId: profile?.userId,
            name: profile?.name,
            profilePictureUrl: profileAvatarSrc,
            handle: profile?.handle,
            profileMode: profile?.profileMode,
          }}
          onViewPeerProfile={openPeerProfileFromMessaging}
        />
      </PostsShellLayout>
      {isMobileLayout && isSocialRoute ? (
        <PostsMobileFloatingChrome
          chromeMode={mobileScrollChromeMode}
          hidden={hideMobileFloatingChrome}
          onHome={openMobileHome}
          onMessages={openMobileMessages}
          onCreate={openMobileCreate}
          onSocial={openMobileSocial}
          onProfile={openMobileProfile}
          isHomeActive={false}
          isMessagesActive
          isCreateActive={false}
          isSocialActive={mobileFollowSheetOpen}
          isProfileActive={false}
          profileAvatarSrc={profileAvatarSrc}
          showProfileAvatar={!!profile?.userId}
          hasUnreadMessages={hasUnreadMessages}
        />
      ) : null}
      <MessagesFollowSheet
        open={mobileFollowSheetOpen}
        onClose={() => setMobileFollowSheetOpen(false)}
        viewerUserId={profile?.userId}
        onSelectUser={handleFollowSheetSelectUser}
      />
    </>
  );
}
